import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Target,
  BookOpen,
  CheckCircle2,
  Plus,
  Trash2,
  Eye,
  ChevronRight,
  Award,
  TrendingUp,
  Sparkles,
  Brain,
  Zap,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DetailedWeeklyPlanPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: 'Physics',
    topic: 'Kinematics',
    student_level: 'hybrid',
    weekly_study_hours: 7,
    daily_study_time: 60,
    goals: [],
    weak_areas: []
  });
  const [goalInput, setGoalInput] = useState('');
  const [weakAreaInput, setWeakAreaInput] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/personalization/premium/my-detailed-plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  };

  const handleCreatePlan = async () => {
    if (!formData.topic.trim()) {
      setError('Lütfen bir konu girin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/personalization/premium/generate-detailed-study-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPlan(data.plan);
        setShowCreateForm(false);
        await fetchPlans();
        // Reset form
        setFormData({
          subject: 'Physics',
          topic: '',
          student_level: 'hybrid',
          weekly_study_hours: 7,
          daily_study_time: 60,
          goals: [],
          weak_areas: []
        });
        setGoalInput('');
        setWeakAreaInput('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Plan oluşturulamadı');
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Error creating plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Bu planı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/personalization/premium/detailed-plan/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchPlans();
        if (selectedPlan && selectedPlan.plan_id === planId) {
          setSelectedPlan(null);
        }
      }
    } catch (err) {
      console.error('Error deleting plan:', err);
    }
  };

  const handleViewPlan = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/personalization/premium/detailed-plan/${planId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPlan(data.plan);
      }
    } catch (err) {
      console.error('Error fetching plan:', err);
    }
  };

  const addGoal = () => {
    if (goalInput.trim() && !formData.goals.includes(goalInput.trim())) {
      setFormData({
        ...formData,
        goals: [...formData.goals, goalInput.trim()]
      });
      setGoalInput('');
    }
  };

  const removeGoal = (goal) => {
    setFormData({
      ...formData,
      goals: formData.goals.filter(g => g !== goal)
    });
  };

  const addWeakArea = () => {
    if (weakAreaInput.trim() && !formData.weak_areas.includes(weakAreaInput.trim())) {
      setFormData({
        ...formData,
        weak_areas: [...formData.weak_areas, weakAreaInput.trim()]
      });
      setWeakAreaInput('');
    }
  };

  const removeWeakArea = (area) => {
    setFormData({
      ...formData,
      weak_areas: formData.weak_areas.filter(a => a !== area)
    });
  };

  const DayCard = ({ day, index }) => {
    const getTypeIcon = (type) => {
      if (type.includes('FRQ')) return <Brain className="h-5 w-5" />;
      if (type.includes('MC')) return <Zap className="h-5 w-5" />;
      if (type.includes('TYT') || type.includes('AYT')) return <Target className="h-5 w-5" />;
      return <BookOpen className="h-5 w-5" />;
    };

    const getTypeColor = (type) => {
      if (type.includes('FRQ')) return 'bg-purple-100 text-purple-700 border-purple-300';
      if (type.includes('MC')) return 'bg-blue-100 text-blue-700 border-blue-300';
      if (type.includes('TYT') || type.includes('AYT')) return 'bg-green-100 text-green-700 border-green-300';
      return 'bg-gray-100 text-gray-700 border-gray-300';
    };

    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium opacity-90">Gün {day.day_number}</div>
              <h3 className="text-xl font-bold">{day.day_name}</h3>
            </div>
            <Calendar className="h-8 w-8 opacity-80" />
          </div>
        </div>

        {/* Main Theme */}
        <div className="p-4 bg-indigo-50 border-b border-indigo-100">
          <div className="flex items-start space-x-2">
            <Sparkles className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-xs text-indigo-600 font-medium uppercase mb-1">Ana Tema</div>
              <p className="text-sm font-semibold text-gray-800">{day.main_theme}</p>
            </div>
          </div>
        </div>

        {/* Study Type & Duration */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${getTypeColor(day.study_type)}`}>
              {getTypeIcon(day.study_type)}
              <span className="text-sm font-medium">{day.study_type}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-600">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">{day.total_duration_minutes} dk</span>
            </div>
          </div>
        </div>

        {/* Learning Objectives */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center space-x-2 mb-3">
            <Target className="h-5 w-5 text-green-600" />
            <h4 className="text-sm font-semibold text-gray-800">Öğrenme Hedefleri</h4>
          </div>
          <ul className="space-y-2">
            {day.learning_objectives && day.learning_objectives.map((objective, idx) => (
              <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{objective}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Warmup */}
        {day.warmup && (
          <div className="p-4 bg-yellow-50 border-b border-yellow-100">
            <div className="flex items-center space-x-2 mb-2">
              <Zap className="h-4 w-4 text-yellow-600" />
              <h4 className="text-sm font-semibold text-gray-800">Isınma ({day.warmup.duration_minutes} dk)</h4>
            </div>
            <ul className="space-y-1 ml-6">
              {day.warmup.activities && day.warmup.activities.map((activity, idx) => (
                <li key={idx} className="text-sm text-gray-700">• {activity}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Main Sessions */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center space-x-2 mb-3">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            <h4 className="text-sm font-semibold text-gray-800">Ana Çalışma Oturumları</h4>
          </div>
          <div className="space-y-3">
            {day.main_sessions && day.main_sessions.map((session, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-800">{session.title}</h5>
                  <div className="flex items-center space-x-1 text-gray-600">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">{session.duration_minutes} dk</span>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-md font-medium">
                    {session.type}
                  </span>
                </div>
                {session.tasks && session.tasks.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-600 mb-1">Görevler:</p>
                    <ul className="space-y-1 ml-3">
                      {session.tasks.map((task, tidx) => (
                        <li key={tidx} className="text-xs text-gray-700">• {task}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {session.example_questions && session.example_questions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">Örnek Sorular:</p>
                    <ul className="space-y-1 ml-3">
                      {session.example_questions.map((q, qidx) => (
                        <li key={qidx} className="text-xs text-gray-600 italic">• {q}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* End of Day Reflection */}
        {day.end_of_day_reflection && day.end_of_day_reflection.length > 0 && (
          <div className="p-4 bg-blue-50">
            <div className="flex items-center space-x-2 mb-2">
              <Award className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-800">Gün Sonu Değerlendirme</h4>
            </div>
            <ul className="space-y-1 ml-6">
              {day.end_of_day_reflection.map((item, idx) => (
                <li key={idx} className="text-sm text-gray-700">• {item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <Sparkles className="h-8 w-8 text-indigo-600" />
                <span>Premium Haftalık Çalışma Planları</span>
              </h1>
              <p className="text-gray-600 mt-1">Yapay zeka destekli, günlük detaylı öğrenme programları</p>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center space-x-2 shadow-md"
            >
              <Plus className="h-5 w-5" />
              <span>Yeni Plan Oluştur</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <TrendingUp className="h-6 w-6 text-indigo-600" />
              <span>Yeni Detaylı Plan Oluştur</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ders</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Physics">Fizik</option>
                  <option value="Chemistry">Kimya</option>
                  <option value="Mathematics">Matematik</option>
                  <option value="Biology">Biyoloji</option>
                </select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Konu *</label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="Örn: Kinematik, Termodinamik"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seviye</label>
                <select
                  value={formData.student_level}
                  onChange={(e) => setFormData({ ...formData, student_level: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="TYT">TYT</option>
                  <option value="AYT">AYT</option>
                  <option value="AP">AP</option>
                  <option value="hybrid">TYT + AP (Hibrit)</option>
                </select>
              </div>

              {/* Weekly Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Haftalık Çalışma Saati</label>
                <input
                  type="number"
                  min="1"
                  max="40"
                  value={formData.weekly_study_hours}
                  onChange={(e) => setFormData({ ...formData, weekly_study_hours: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Daily Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Günlük Çalışma Süresi (dakika)</label>
                <input
                  type="number"
                  min="15"
                  max="240"
                  step="15"
                  value={formData.daily_study_time}
                  onChange={(e) => setFormData({ ...formData, daily_study_time: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Goals */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Hedeflerim</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                  placeholder="Bir hedef yazın ve Enter'a basın"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={addGoal}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.goals.map((goal, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{goal}</span>
                    <button onClick={() => removeGoal(goal)} className="hover:text-green-900">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Weak Areas */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zorlandığım Konular</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={weakAreaInput}
                  onChange={(e) => setWeakAreaInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addWeakArea()}
                  placeholder="Zayıf bir alan yazın ve Enter'a basın"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={addWeakArea}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.weak_areas.map((area, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{area}</span>
                    <button onClick={() => removeWeakArea(area)} className="hover:text-orange-900">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="mt-6 flex space-x-4">
              <button
                onClick={handleCreatePlan}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
              >
                {loading ? 'Oluşturuluyor...' : 'Plan Oluştur'}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                İptal
              </button>
            </div>
          </div>
        )}

        {/* Plans List */}
        {!selectedPlan && plans.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <BookOpen className="h-6 w-6 text-indigo-600" />
              <span>Planlarım ({plans.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.topic}</h3>
                      <p className="text-sm text-gray-600">{plan.subject} • {plan.level}</p>
                    </div>
                    <Calendar className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{plan.days?.length || 7} gün</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Target className="h-4 w-4" />
                      <span>Oluşturulma: {new Date(plan.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewPlan(plan.plan_id)}
                      className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center justify-center space-x-2 text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Görüntüle</span>
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan.plan_id)}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Plan View */}
        {selectedPlan && (
          <div>
            <div className="mb-8">
              <button
                onClick={() => setSelectedPlan(null)}
                className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-2 mb-4"
              >
                <ChevronRight className="h-5 w-5 rotate-180" />
                <span>Planlarıma Dön</span>
              </button>
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedPlan.topic}</h2>
                    <div className="flex items-center space-x-4 text-gray-600">
                      <span className="flex items-center space-x-1">
                        <BookOpen className="h-4 w-4" />
                        <span className="text-sm">{selectedPlan.subject}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Target className="h-4 w-4" />
                        <span className="text-sm">{selectedPlan.level}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">{selectedPlan.days?.length || 7} gün</span>
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeletePlan(selectedPlan.plan_id)}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Planı Sil</span>
                  </button>
                </div>

                {selectedPlan.weekly_summary && (
                  <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-indigo-900 mb-2">Haftalık Özet</h3>
                    <p className="text-sm text-gray-700">{selectedPlan.weekly_summary}</p>
                  </div>
                )}

                {selectedPlan.tips_for_success && selectedPlan.tips_for_success.length > 0 && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-green-900 mb-2 flex items-center space-x-2">
                      <Award className="h-4 w-4" />
                      <span>Başarı İpuçları</span>
                    </h3>
                    <ul className="space-y-1">
                      {selectedPlan.tips_for_success.map((tip, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start space-x-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {selectedPlan.days && selectedPlan.days.map((day, index) => (
                <DayCard key={index} day={day} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedPlan && plans.length === 0 && !showCreateForm && (
          <div className="text-center py-16">
            <Calendar className="h-20 w-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz plan oluşturmadınız</h3>
            <p className="text-gray-600 mb-6">Yapay zeka destekli detaylı çalışma planınızı hemen oluşturun!</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all inline-flex items-center space-x-2 shadow-md"
            >
              <Plus className="h-5 w-5" />
              <span>İlk Planımı Oluştur</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedWeeklyPlanPage;
